import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { exportOriginalLocalData, initializeLocalData, inspectLocalData, recoverLatestLocalSnapshot, resetLocalUserData, type LocalDataHealth } from './db.js';
import { encryptBackup } from './backup.js';

export function LocalDataGate({ children, onReady }: { children: ReactNode; onReady: () => void }) {
  const [health, setHealth] = useState<LocalDataHealth>();
  const [error, setError] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const readyNotified = useRef(false);

  const inspect = useCallback(async () => {
    setError('');
    try {
      await initializeLocalData();
      const next = await inspectLocalData();
      setHealth(next);
      if (next.ok && !readyNotified.current) { readyNotified.current = true; onReady(); }
    } catch { setHealth({ ok: false, schemaVersion: 0, invalidRecords: ['database:open'], brokenShiftReferences: [] }); }
  }, [onReady]);

  useEffect(() => {
    const timer = window.setTimeout(() => { void inspect(); }, 0);
    return () => window.clearTimeout(timer);
  }, [inspect]);

  async function recover() {
    try { await recoverLatestLocalSnapshot(); await inspect(); }
    catch { setError('복구 가능한 로컬 스냅샷을 찾지 못했어요. 내보낸 암호화 백업 파일은 초기화 후 설정에서 복원할 수 있어요.'); }
  }

  async function preserveOriginal() {
    try {
      const bundle = await encryptBackup(await exportOriginalLocalData());
      const url = URL.createObjectURL(new Blob([JSON.stringify(bundle.envelope)], { type: 'application/json' }));
      const anchor = document.createElement('a'); anchor.href = url; anchor.download = 'wolhasudang-original-encrypted.json'; anchor.click(); URL.revokeObjectURL(url);
      setRecoveryCode(bundle.recoveryCode); setError('읽을 수 있는 현재 원본을 암호화 파일로 만들었어요. 복구 코드를 파일과 따로 보관하세요. 손상된 원본은 그대로 보관되며 유효한 복원 파일이라는 뜻은 아니에요.');
    } catch { setError('원본 파일을 만들지 못했어요. 기록을 지우지 말고 다시 확인해주세요.'); }
  }

  async function reset() {
    if (!window.confirm('현재 기기의 손상된 기록을 초기화할까요? 내보낸 백업이 없으면 되돌릴 수 없습니다.')) return;
    if (!window.confirm('정말 초기화할까요? 로컬 프로필·근무·수당·명세·스냅샷이 삭제됩니다.')) return;
    try { await resetLocalUserData(); await inspect(); }
    catch { setError('초기화하지 못했어요. 저장 공간을 확인하고 다시 시도해주세요.'); }
  }

  if (!health) return <main className="content content-full"><section aria-busy="true"><p className="eyebrow">로컬 기록 확인 중</p><div className="skeleton-block" /></section></main>;
  if (health.ok) return children;
  return <main className="content content-full"><section className="policy"><p className="eyebrow">기록 복구 필요</p><h1>기기의 저장 데이터를 안전하게 열지 못했어요</h1><article className="warning-card"><strong>기록을 지우지 않고 사용을 멈췄어요</strong><p>확인이 필요한 기록 {health.invalidRecords.length}개 · 연결이 끊긴 근무 {health.brokenShiftReferences.length}개가 있어요. 아래 복구를 먼저 시도해주세요.</p></article><div className="form-actions"><button className="primary-button" onClick={() => void recover()}>최근 안전한 기록으로 복구</button><button className="secondary-button" onClick={() => void inspect()}>다시 확인</button><button className="secondary-button" onClick={() => void preserveOriginal()}>현재 원본 파일 보관</button></div><button className="danger-row" onClick={() => void reset()}>복구할 수 없을 때 기록 초기화</button>{error && <p className="warning-card" role="alert">{error}</p>}{recoveryCode && <label>원본 파일 복구 코드<input readOnly value={recoveryCode} /></label>}</section></main>;
}
